import React, {useEffect, useState} from 'react';
import {Box, Center} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import {StackParamList} from '../../navigators';
import ExternalTemplate from './../../components/templates/external';
import {PrimaryButton} from './../../components/buttons';
import {MaskedInput, RifInput} from './../../components/inputs';
import {TextInput as Input} from './../../components/inputs/text';
import DropDownInput from './../../components/inputs/dropdown';
import IDropDownOption from '../../interfaces/ui/dropdown-option.interface';
import {ISignupForm} from '../../interfaces/ui';
import {checkIsValidEmail} from '../../utils/validations';
import useToast from '../../hooks';
import {useContextState} from '../../context/user';
import {SignUpState} from '../../constants/enumerations/state.enum';
import {SpinnerModal} from '../../components/modals';
import {isWebApp} from '../../utils/platform/is-web';

type ScreenProps = NativeStackScreenProps<StackParamList, 'SignUp'>;

export const SignUpScreen = ({navigation}: ScreenProps) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm({
    defaultValues: {
      company: '',
      identity: '',
      entity: '',
      email: '',
      firstName: '',
      lastName: '',
    } as ISignupForm,
  });
  const [isLoading, setIsLoading] = useState(false);
  const {entities, signup} = useContextState();
  const [entityList, setEntityList] = useState([] as IDropDownOption[]);

  useEffect(() => {
    if (entities) {
      setEntityList(
        entities.map(entity => ({label: entity.title, value: entity.value})),
      );
    }
  }, [entities]);

  const {showServerError, showInputsError, showSignUpError} = useToast();

  const onSubmit: SubmitHandler<ISignupForm> = async (data: ISignupForm) => {
    setIsLoading(true);
    const result = await signup(data);
    setIsLoading(false);
    if (result === SignUpState.success) {
      navigation.navigate('RequestSuccess');
    } else if (result === SignUpState.rejected) {
      showSignUpError();
    } else {
      showServerError();
    }
  };

  const onInvalid: SubmitErrorHandler<ISignupForm> = async (
    fieldErrors: FieldErrors<ISignupForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleGoBack = async () => {
    navigation.navigate('SignIn');
  };

  return (
    <ExternalTemplate
      title="sign-up-title"
      summary="sign-up-summary"
      backButton={{
        onPress: () => handleGoBack(),
      }}>
      <SpinnerModal show={isLoading} />
      <KeyboardAwareScrollView
        enableOnAndroid={true}
        showsVerticalScrollIndicator={isWebApp}>
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%" py={10}>
            <Input
              control={control}
              name="company"
              label="sign-up-company"
              error={errors.company}
              rules={{
                required: 'form-is-required',
              }}
            />
            <RifInput
              control={control}
              name="identity"
              label="sign-up-identity"
              error={errors.identity}
              rules={{
                required: 'form-is-required',
              }}
            />
            <DropDownInput
              control={control}
              name="entity"
              label="sign-up-entity"
              options={entityList}
              error={errors.entity}
              rules={{
                required: 'form-is-required',
              }}
            />
            <MaskedInput
              control={control}
              name="firstName"
              label="sign-up-first-name"
              mask="people-names"
              error={errors.firstName}
              rules={{
                required: 'form-is-required',
              }}
            />
            <MaskedInput
              control={control}
              name="lastName"
              label="sign-up-last-name"
              mask="people-names"
              error={errors.lastName}
              rules={{
                required: 'form-is-required',
              }}
            />
            <Input
              control={control}
              name="email"
              type="email"
              label="sign-up-last-email"
              error={errors.email}
              rules={{
                required: 'form-is-required',
                validate: email => checkIsValidEmail(email) || 'form-is-email',
              }}
            />
            <Box mt={5}>
              <PrimaryButton
                label="sign-up-button-send"
                onPress={handleSubmit(onSubmit, onInvalid)}
              />
            </Box>
          </Box>
        </Center>
      </KeyboardAwareScrollView>
    </ExternalTemplate>
  );
};
